import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Save } from 'lucide-react';
import { getTemplate, getVariables, updateTemplate, createTemplate } from 'Services/storeService';
import { message } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import logger from 'utils/logger';
import {
  tokenToHtml,
  htmlToToken,
  buildVariableChipHtml,
  normalizeVariableCode,
  variableChipLabel,
} from 'utils/communicationTemplateTokens';

// `readOnly` (role lacks communications.manage) shows the message without the
// editing surface or the save control, so no template write can be issued.
const SmsEditorModal = ({ node, flowId, readOnly = false, onClose, onSave }) => {
  const editorRef = useRef(null);
  const [variables, setVariables] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rawContent, setRawContent] = useState('');

  useEffect(() => {
    const fetchVars = async () => {
      try {
        const res = await getVariables(flowId);
        if (res?.data?.data && Array.isArray(res.data.data)) {
          setVariables(
            res.data.data.map((v) => ({
              label: v.description || v.code || v.name,
              value: normalizeVariableCode(v.code || v.key),
              description: v.description || v.example_value || '',
              type: v.type || undefined,
            }))
          );
        }
      } catch (err) {
        logger.error('Failed to get variables for SMS editor', err);
      }
    };
    fetchVars();
  }, [flowId]);

  useEffect(() => {
    const fetchTemplate = async () => {
      const templateId = node?.data?.templateId;
      if (templateId && templateId !== 'new-template') {
        setIsLoading(true);
        try {
          const res = await getTemplate(templateId, 'sms');
          const responseData = res?.data?.data || {};
          const tmpl = responseData.draft_version || responseData.template?.active_version || responseData;
          const nextContent = tmpl.message || '';
          setRawContent(nextContent);
        } catch (err) {
          logger.error('Failed to fetch SMS template', err);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchTemplate();
  }, [node]);

  useEffect(() => {
    if (!editorRef.current || isLoading) return;
    if (document.activeElement === editorRef.current) return;

    editorRef.current.innerHTML = tokenToHtml(rawContent, variables);

    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(editorRef.current);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }, [rawContent, variables, isLoading]);

  const handleInput = useCallback(() => {
    if (editorRef.current) setRawContent(htmlToToken(editorRef.current.innerHTML));
  }, []);

  const insertVariable = useCallback(
    (variable) => {
      if (!editorRef.current) return;
      editorRef.current.focus();
      const chipHtml = buildVariableChipHtml(variable);
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const div = document.createElement('div');
        div.innerHTML = chipHtml;
        const frag = document.createDocumentFragment();
        let last;
        div.childNodes.forEach((c) => {
          last = frag.appendChild(c.cloneNode(true));
        });
        range.insertNode(frag);
        if (last) {
          range.setStartAfter(last);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      } else {
        editorRef.current.innerHTML += chipHtml;
      }
      setRawContent(htmlToToken(editorRef.current.innerHTML));
    },
    []
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  };

  const handleSave = async () => {
    const templateId = node?.data?.templateId;
    if (!templateId || templateId === 'new-template') {
      onSave();
      return;
    }
    const messageContent = editorRef.current ? htmlToToken(editorRef.current.innerHTML) : rawContent;
    setIsSaving(true);
    const payload = {
      name: node?.data?.label || 'SMS Template',
      message: messageContent,
      is_draft: true,
    };
    try {
      await updateTemplate(templateId, 'sms', payload);
      message.success('SMS template saved!');
      onSave();
    } catch (err) {
      if (
        err?.response?.status === 404 ||
        err?.response?.status === 403 ||
        err?.response?.data?.message?.includes('not found')
      ) {
        try {
          const res = await createTemplate(uuidv4(), 'sms', payload.name);
          const newId = res.data?.data?.template?.id || res.data?.data?.id || res.data?.id;
          await updateTemplate(newId, 'sms', payload);
          message.success('SMS template saved to your store!');
          onSave(newId);
        } catch (e) {
          logger.error('Failed to create/save SMS template:', e);
          message.error('Could not save SMS template');
        }
      } else {
        logger.error('Failed to save SMS template:', err);
        message.error('Could not save SMS template');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const charCount = rawContent.length;

  return (
    <div
      className="sms-editor-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        className="sms-editor-modal"
        style={{
          width: '860px',
          maxWidth: '90%',
          backgroundColor: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f7fafc',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1a202c' }}>
            {readOnly ? 'View SMS' : 'Edit SMS'}: {node?.data?.label || 'SMS'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#64748b',
            }}
          >
            <X size={20} />
          </button>
        </div>
        <div className="sms-editor-layout" style={{ display: 'flex', minHeight: '320px' }}>
          <div style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#4a5568',
              }}
            >
              Message Body
            </label>
            {isLoading ? (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#718096',
                }}
              >
                Loading...
              </div>
            ) : (
              <div
                ref={editorRef}
                contentEditable={!readOnly}
                suppressContentEditableWarning
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                data-placeholder="Type message here..."
                style={{
                  flex: 1,
                  minHeight: '150px',
                  padding: '12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  outline: 'none',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              />
            )}
            <style>{`[data-placeholder]:empty:before { content: attr(data-placeholder); color: #a0aec0; }`}</style>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#718096', textAlign: 'right' }}>
              {charCount} chars · {Math.ceil(charCount / 160) || 1} segment
              {Math.ceil(charCount / 160) > 1 ? 's' : ''}
            </div>
          </div>
          <div
            className="sms-editor-sidebar"
            style={{
              width: '280px',
              minWidth: '280px',
              borderLeft: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              padding: '16px',
              overflowY: 'auto',
              maxHeight: '500px',
            }}
          >
            <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Variables</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {variables.map((v) => (
                <button
                  key={v.value}
                  disabled={readOnly}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!readOnly) insertVariable(v);
                  }}
                  title={v.value}
                  style={{
                    padding: '8px',
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      fontWeight: '600',
                      color: '#1a202c',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {variableChipLabel(v, v.value)}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '11px',
                      color: '#718096',
                      marginTop: '4px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {v.value}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            backgroundColor: '#f7fafc',
          }}
        >
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px' }}
          >
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            style={{
              padding: '8px 16px',
              background: '#48bb78',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              opacity: isSaving || isLoading ? 0.7 : 1,
            }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmsEditorModal;
